<template>
  <FriedCard
    ref="overviewCard"
    :class="[
      'overview-card',
      { 'example-overview': showExample, mobile: isXXSmallScreen, expanded },
    ]"
  >
    <div class="title-container">
      <div class="title">
        <FriedH3>{{ t('MAP_action_plan_overview') }}</FriedH3>
        <div class="pill-button-container">
          <ProgressPill :percentage="progressPercentage" />
          <FriedIconButton data-external="milestone-collapse-button" @click="expanded = !expanded">
            <FriedIcon :class="{ expanded }" icon="chevron-down" />
          </FriedIconButton>
        </div>
        <TransitionFadeInOut>
          <div
            v-show="!expanded && !isXXSmallScreen"
            :class="['milestone-overview-container', { expanded }]"
          >
            <MilestoneCircle
              v-for="(milestone, index) in actionPlan.milestones"
              :key="milestone.id"
              :milestone="milestone"
              :milestone-number="index + 1"
              :first-active="milestone.id === firstActiveMilestoneId"
              :active="isMilestoneActive(milestone)"
              :completed="isCompleted(milestone)"
              :last="index === actionPlan.milestones.length - 1"
              :show-details="false"
              @scroll-to-milestone="scrollToMilestone"
            />
          </div>
        </TransitionFadeInOut>
      </div>

      <div v-if="!isXXSmallScreen" class="action-plan-progress-details">
        <div :class="['action-plan-completion', { completed: allMilestonesCompleted }]">
          <FriedH3>{{ progress.completedMilestoneCount }} / {{ progress.milestoneCount }}</FriedH3>
          {{ t('MAP_milestones_completed') }}
        </div>
        <div :class="['action-plan-completion', { completed: allTasksCompleted }]">
          <FriedH3>{{ progress.completedTaskCount }} / {{ progress.taskCount }}</FriedH3>
          {{ t('MAP_tasks_completed') }}
        </div>
      </div>
    </div>
    <hr v-show="expanded" />
    <TransitionExpand>
      <div
        v-show="expanded"
        ref="milestoneOverviewContainer"
        :class="['milestone-overview-container', { expanded }]"
      >
        <ScrollableContainerIndicator
          v-if="milestoneOverviewContainer"
          :scrollable-container="milestoneOverviewContainer"
          left-aligned
          horizontally-positioned
          show-button
          :button-tooltip="t('map_see_more_button')"
          @click="scroll(ScrollDirection.Left)"
        />
        <MilestoneCircle
          v-for="(milestone, index) in actionPlan.milestones"
          :id="`milestone-circle-${milestone.id}`"
          :key="milestone.id"
          :milestone="milestone"
          :milestone-number="index + 1"
          :first-active="milestone.id === firstActiveMilestoneId"
          :active="isMilestoneActive(milestone)"
          :completed="isCompleted(milestone)"
          :last="index === actionPlan.milestones.length - 1"
          :milestone-overview-container="milestoneOverviewContainer"
          :total-milestone-count="actionPlan.milestones.length"
          :width="milestoneCircleWidth"
          :focused="milestoneInFocusIndex === index"
          @scroll-to-milestone="scrollToMilestone"
        />
        <ScrollableContainerIndicator
          v-if="milestoneOverviewContainer"
          :scrollable-container="milestoneOverviewContainer"
          right-aligned
          horizontally-positioned
          show-button
          :button-tooltip="t('map_see_more_button')"
          @click="scroll(ScrollDirection.Right)"
        />
      </div>
    </TransitionExpand>
  </FriedCard>
</template>
<script lang="ts">
import type { PropType, Ref } from 'vue';
import { computed, defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { TransitionFadeInOut } from '@getaccept/fried-tofu';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import ScrollableContainerIndicator from '@getaccept/lib-shared/src/components/ScrollableContainerIndicator.vue';
import { useThrottleFn } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { TaskStatus } from '../types/enums/task-status';
import type { ActionPlan, Milestone } from '../types/action-plan';
import MilestoneHelper from '../helpers/milestone.helper';
import MilestoneCircle from './MilestoneCircle.vue';
import ProgressPill from './ProgressPill.vue';

export default defineComponent({
  components: {
    MilestoneCircle,
    ProgressPill,
    TransitionFadeInOut,
    ScrollableContainerIndicator,
  },
  props: {
    actionPlan: {
      type: Object as PropType<ActionPlan>,
      required: true,
    },
    showCompleted: {
      type: Boolean,
    },
    showExample: {
      type: Boolean,
    },
    activeMilestone: {
      type: String,
      default: null,
    },
  },
  emits: ['set-active-milestone'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const progress = computed(() => MilestoneHelper.getActionPlanProgress(props.actionPlan));
    const progressPercentage = computed(() =>
      MilestoneHelper.getActionPlanProgressPercentage(props.actionPlan)
    );
    const { isXXSmallScreen } = useBreakpoints();
    const allMilestonesCompleted = computed(
      () => progress.value.completedMilestoneCount === progress.value.milestoneCount
    );

    const allTasksCompleted = computed(
      () =>
        progress.value.completedTaskCount === progress.value.taskCount &&
        progress.value.taskCount !== 0
    );

    const hideMilestoneInfo = (milestone: Milestone, index: number) =>
      MilestoneHelper.isCompleted(milestone) &&
      !props.showCompleted &&
      index !== props.actionPlan.milestones.length - 1;

    const scrollToMilestone = (id: string) => {
      const element = document.getElementById(id);

      if (!element) {
        return;
      }
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emit('set-active-milestone', id);
    };

    const firstActiveMilestoneId = computed(() =>
      MilestoneHelper.getFirstActiveMilestoneId(props.actionPlan)
    );

    const route = useRoute();

    const hasMilestoneOrTaskId = ref(Boolean(route.params.itemId || route.query.taskId));
    const expanded = ref(!hasMilestoneOrTaskId.value);
    const milestoneOverviewContainer: Ref<HTMLDivElement> = null;
    const milestoneCircleWidth = computed(() => {
      if (isXXSmallScreen.value) {
        return 104;
      }

      return 208;
    });

    const milestoneInFocusIndex = ref();

    enum ScrollDirection {
      Left = 'left',
      Right = 'right',
    }

    const scrollMobile = (direction: ScrollDirection) => {
      if (direction === ScrollDirection.Left) {
        milestoneInFocusIndex.value -= 1;
      } else {
        milestoneInFocusIndex.value += 1;
      }
      const circle = document.getElementById(
        `milestone-circle-${props.actionPlan.milestones[milestoneInFocusIndex.value].id}`
      );
      if (circle) {
        circle.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    };

    const scroll = (direction: ScrollDirection) => {
      if (!milestoneOverviewContainer.value) {
        return;
      }

      const { scrollLeft } = milestoneOverviewContainer.value;

      const indexCheck =
        direction === ScrollDirection.Left
          ? milestoneInFocusIndex.value - 1 >= 0
          : milestoneInFocusIndex.value + 1 < props.actionPlan.milestones.length;

      if (isXXSmallScreen.value && indexCheck) {
        scrollMobile(direction);
        return;
      }

      const scrollAmount =
        direction === ScrollDirection.Left
          ? scrollLeft - milestoneCircleWidth.value * 3
          : scrollLeft + milestoneCircleWidth.value * 3;

      milestoneOverviewContainer.value.scroll({
        left: scrollAmount,
        behavior: 'smooth',
      });
    };

    const throttledSetExpanded = useThrottleFn(isIntersecting => {
      if (hasMilestoneOrTaskId.value) {
        hasMilestoneOrTaskId.value = false;
        return;
      }
      expanded.value = isIntersecting;
    }, 300);

    const observer = new IntersectionObserver(
      ([{ isIntersecting }]) => {
        throttledSetExpanded(isIntersecting);
      },
      {
        threshold: 0.2,
      }
    );

    onMounted(() => {
      if (isXXSmallScreen) {
        milestoneInFocusIndex.value = props.actionPlan.milestones.findIndex(
          milestone => milestone.id === firstActiveMilestoneId.value
        );
        const circle = document.getElementById(`milestone-circle-${firstActiveMilestoneId.value}`);
        if (circle) {
          circle.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }
      const mapTitleContainer = document.getElementById('mutual-action-plan-title-container');

      if (mapTitleContainer) {
        observer.observe(mapTitleContainer);
      }
    });

    onBeforeUnmount(() => {
      observer.disconnect();
    });

    return {
      progress,
      allTasksCompleted,
      allMilestonesCompleted,
      isXXSmallScreen,
      hideMilestoneInfo,
      isCompleted: MilestoneHelper.isCompleted,
      scrollToMilestone,
      getStatus: MilestoneHelper.getStatus,
      t,
      TaskStatus,
      firstActiveMilestoneId,
      isMilestoneActive: MilestoneHelper.isMilestoneActive,
      expanded,
      progressPercentage,
      milestoneOverviewContainer,
      scroll,
      ScrollDirection,
      milestoneCircleWidth,
      milestoneInFocusIndex,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '../scss/milestone';

$milestone-card-width: 15rem;
$completed-milestone-card-width: 2.5rem;

.overview-card {
  padding: var(--spacing-150);
  margin-bottom: var(--spacing-100);
  box-shadow: none;
  border: 1px solid var(--gray-89);
  position: sticky;
  top: var(--spacing-100);
  z-index: 2;

  &:not(.expanded) {
    box-shadow: var(--shadow-4);
  }

  @media screen and (max-width: $xs) {
    top: 0;
  }

  &.expanded {
    .title {
      svg {
        transform: rotate(-180deg);
      }
    }
  }

  &.mobile {
    padding: var(--spacing-100);
    background-color: var(--white);
    margin-bottom: var(--spacing-200);
    border-radius: 0;

    .pill-button-container {
      flex-direction: row-reverse;
    }
  }

  &.example-overview {
    opacity: 0.5;
    pointer-events: none;
  }

  .title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      display: flex;
      gap: var(--spacing-100);
      align-items: center;

      .pill-button-container {
        display: flex;
        gap: var(--spacing-100);
      }

      svg {
        transition: transform 0.3s;
      }
    }

    .action-plan-progress-details {
      display: flex;
      gap: var(--spacing-100);

      .action-plan-completion {
        text-align: center;

        &.completed {
          .heading-3 {
            color: var(--success-dark);
          }
        }
      }
    }
  }

  .milestone-overview-container {
    display: flex;

    &.expanded {
      padding-bottom: var(--spacing-100);
      overflow: auto;
    }

    &:not(.expanded) {
      overflow: unset;
    }

    @media screen and (max-width: $xs) {
      &.expanded {
        overflow: hidden;
        padding-left: 4rem;
      }
    }
  }

  hr {
    border: 0;
    border-top: 1px solid var(--gray-96);
    margin: var(--spacing-100) 0;
  }
}
</style>
